import { useCallback, useEffect, useRef, useState } from "react";
import {
  construirTicket, estacion as api, guardarBaseImpresion, guardarEmparejamiento,
  leerBaseImpresion, leerEmparejamiento, leerEtiqueta, leerSueltasPendientes,
  guardarSueltasPendientes, obtenerImpresora, olvidarEmparejamiento,
} from "../../lib/impresion";

/**
 * La impresora de comandas, vista desde la Barra.
 *
 * La barra tiene UNA impresora y varias pantallas mirando la misma cola. Quién imprime
 * y qué comandas ya salieron viven en el SERVIDOR, que es lo único que ven todas: sin
 * eso, cada aparato va ciego y no hay ni relevo ni forma de evitar duplicados.
 *
 * REGLA DE ORO: imprimir NUNCA tumba la cola de comandas. Todo error se traga aquí y se
 * cuenta por `onFallo`; el cobro y el "Servida ✓" tienen que seguir funcionando con la
 * impresora apagada, sin papel o rota.
 */

/** Tickets que esperan a que vuelva la impresora. Más allá, lo viejo ya no interesa. */
const MAX_COLA = 10;

/** Reintento silencioso de reconexión, colgado del sondeo que ya existe. */
const REINTENTO_MS = 30000;

/** Respiro entre tickets: estas térmicas pierden el segundo si se les encadena. */
const ENTRE_TICKETS_MS = 1500;

export default function useImpresoraComandas(venueID, { onFallo, authHeaders } = {}) {
  const [conectada, setConectada] = useState(() => obtenerImpresora().conectada());
  const [papel, setPapel] = useState(null);
  const [emparejada, setEmparejada] = useState(null);
  const [enCola, setEnCola] = useState(0);
  const [conectando, setConectando] = useState(false);
  // Quién está imprimiendo, según el servidor. Puede ser otro aparato.
  const [estacion, setEstacion] = useState(null);

  const impresoraRef = useRef(null);
  const colaRef = useRef([]);
  const ultimoIntentoRef = useRef(0);
  const ultimoLatidoRef = useRef(0);
  // Lo que esta estación ya consideró. Comparar marcas de tiempo era frágil por
  // milisegundos; una lista de identificadores no admite discusión. Se carga del
  // dispositivo para que una recarga del móvil no la deje creyéndose recién estrenada.
  const baseRef = useRef(null);
  //  ¿Se estrena esta estación en este aparato? Una señal propia y no el tamaño del
  //  conjunto: con la barra VACÍA al abrir se quedaría a cero para siempre.
  const estrenoRef = useRef(true);
  const onFalloRef = useRef(onFallo);
  onFalloRef.current = onFallo;
  const cabecerasRef = useRef(authHeaders);
  cabecerasRef.current = authHeaders;

  if (impresoraRef.current === null) {
    impresoraRef.current = obtenerImpresora({
      onEstado: (nuevo) => setPapel(nuevo),
      onDesconexion: () => setConectada(false),
    });
  }
  const impresora = impresoraRef.current;

  useEffect(() => {
    setEmparejada(leerEmparejamiento(venueID));
    const guardadas = leerBaseImpresion(venueID);
    baseRef.current = new Set(guardadas || []);
    estrenoRef.current = guardadas === null;
  }, [venueID]);

  const avisar = useCallback((order, motivo) => {
    if (onFalloRef.current) onFalloRef.current(order, motivo);
  }, []);

  /**
   * El chip dice la VERDAD, preguntando en vivo.
   *
   * Fiarse solo del evento de desconexión lo dejaba verde con la impresora muerta: ese
   * aviso se pierde con facilidad cuando el móvil duerme la pestaña.
   */
  const sincronizarEstado = useCallback(() => {
    const viva = impresora.conectada();
    setConectada((antes) => (antes === viva ? antes : viva));
  }, [impresora]);

  /** Manda el ticket habiendo ganado antes el turno. Devuelve false si no salió. */
  /**
   * Devuelve la comanda a la cola del servidor, y si no se puede, lo APUNTA.
   *
   * `reclamar` pone `printed_at` antes de imprimir —así dos aparatos no sacan el mismo
   * papel—, así que soltarla es lo único que deshace esa marca. Si la impresión falla y
   * la red también, la comanda se queda marcada como IMPRESA sin haber salido: el
   * ticket fantasma. Nadie la vuelve a mirar, porque para el servidor ya está hecha.
   *
   * Antes esto era un `await` a secas: si fallaba, el fantasma era permanente. Ahora la
   * suelta pendiente se guarda en el dispositivo y se reintenta en cada sondeo.
   */
  const soltarConReintento = useCallback(async (orderId) => {
    try {
      await api.soltar(orderId, cabecerasRef.current);
      return true;
    } catch {
      const pendientes = leerSueltasPendientes(venueID);
      guardarSueltasPendientes(venueID, [...pendientes, String(orderId)]);
      return false;
    }
  }, [venueID]);

  /** Reintenta las sueltas que no llegaron. Se llama en cada sondeo. */
  const drenarSueltasPendientes = useCallback(async () => {
    const pendientes = leerSueltasPendientes(venueID);
    if (pendientes.length === 0) return;
    const quedan = [];
    for (const id of pendientes) {
      try {
        // eslint-disable-next-line no-await-in-loop -- en serie: son pocas y el orden
        // no importa; en paralelo se le echa encima al servidor sin necesidad.
        await api.soltar(id, cabecerasRef.current);
      } catch {
        quedan.push(id);
      }
    }
    guardarSueltasPendientes(venueID, quedan);
  }, [venueID]);

  const mandar = useCallback(async (order) => {
    try {
      await impresora.imprimir(construirTicket(order));
      return true;
    } catch {
      // No salió: se devuelve la comanda a la cola del servidor para que la coja
      // cualquiera, en vez de quedarse marcada como impresa sin papel.
      await soltarConReintento(order.id);
      return false;
    }
  }, [impresora, soltarConReintento]);

  const encolar = useCallback((order) => {
    const completa = [...colaRef.current.filter((o) => o.id !== order.id), order];
    const cabe = completa.slice(-MAX_COLA);

    // Lo que NO cabe se DESCONSIDERA en vez de evaporarse. Antes el `.slice` tiraba las
    // más viejas y ahí morían: ya estaban en `base`, su `printed_at` seguía a NULL y
    // nadie volvía a mirarlas. Con la impresora caída y 15 comandas, las 5 primeras se
    // perdían y el chip decía «10 sin imprimir», nunca 15.
    // Sacándolas de `base`, el siguiente sondeo vuelve a cogerlas: la cola sigue acotada
    // pero el trabajo no se olvida.
    const fuera = completa.slice(0, Math.max(0, completa.length - MAX_COLA));
    if (fuera.length && baseRef.current) {
      for (const o of fuera) baseRef.current.delete(String(o.id));
      guardarBaseImpresion(venueID, Array.from(baseRef.current));
    }

    colaRef.current = cabe;
    setEnCola(colaRef.current.length);
  }, [venueID]);

  /**
   * ¿Le toca el papel a esta comanda?
   *
   * Sale por papel si nadie la ha impreso —eso lo dice el servidor, no este aparato— y
   * o bien llegó con la pantalla ya abierta, o bien alguien pidió repetirla desde otra
   * pantalla, que es la vía del camarero que no tiene la impresora al lado.
   */
  const leToca = useCallback((order) => {
    if (!order?.id || order.printed_at) return false;
    if (order.reprint_requested_at) return true;
    return !baseRef.current?.has(String(order.id));
  }, []);

  /** Imprime una comanda ganando antes el turno en el servidor. */
  const imprimirUna = useCallback(async (order) => {
    if (!(await api.reclamar(order.id, cabecerasRef.current))) return false; // la cogió otro
    if (await mandar(order)) return true;
    encolar(order);
    avisar(order, "No se pudo imprimir el ticket. La comanda está bien: sale al reconectar.");
    return false;
  }, [mandar, encolar, avisar]);

  /**
   * Repasa la cola entera en cada sondeo y saca lo que falte.
   *
   * En serie y no en paralelo: encadenar tickets sin respiro desborda el buffer de estas
   * térmicas y el segundo se pierde, aceptado y confirmado.
   */
  const revisarCola = useCallback(async (orders) => {
    if (!impresora.disponible || !leerEmparejamiento(venueID)) return;

    // Lo PRIMERO: deshacer las marcas de «impresa» que se quedaron colgadas. Si no, esas
    // comandas siguen siendo fantasmas y ni siquiera aparecen como pendientes aquí.
    await drenarSueltasPendientes();

    const base = baseRef.current || new Set();
    // El ESTRENO no imprime: al abrir la pantalla por primera vez en este aparato, la
    // cola entera está "sin imprimir" y saldrían cuarenta tickets ya servidos.
    const estreno = estrenoRef.current;
    estrenoRef.current = false;
    // Lo que se encoló con la impresora caída sigue en juego aunque ya esté "considerado":
    // sin esto, un ticket que no pudo salir quedaba huérfano para siempre y el vaciado al
    // reconectar no ocurría nunca.
    const enEspera = new Set(colaRef.current.map((o) => String(o.id)));
    const pendientes = estreno
      ? []
      : (orders || []).filter((o) => !o.printed_at && (leToca(o) || enEspera.has(String(o.id))));

    // Todo lo que se ve queda considerado, se imprima o no.
    for (const o of orders || []) base.add(String(o.id));
    baseRef.current = base;
    guardarBaseImpresion(venueID, Array.from(base));

    if (pendientes.length === 0) return;

    if (!impresora.conectada()) {
      for (const order of pendientes) encolar(order);
      return;
    }

    for (const [i, order] of pendientes.entries()) {
      // eslint-disable-next-line no-await-in-loop -- en serie a propósito: los tickets
      // salen en orden y la impresora necesita respirar entre trabajos.
      const salio = await imprimirUna(order);
      if (!salio) {
        // Las de DETRÁS se encolan. Antes solo se cortaba el bucle, y como todas las
        // vistas ya estaban marcadas como «consideradas», nadie volvía a mirarlas: no
        // salían jamás, ni al cambiar el rollo, y en pantalla seguían como pendientes
        // normales, sin marca. Viernes, tres comandas, se acaba el papel en la primera
        // → la 2 y la 3 se perdían hasta que reclamaba el socio.
        // (La que ha fallado ya la encoló `imprimirUna`.)
        for (const restante of pendientes.slice(i + 1)) encolar(restante);
        break;
      }
      colaRef.current = colaRef.current.filter((o) => o.id !== order.id);
      setEnCola(colaRef.current.length);
      // eslint-disable-next-line no-await-in-loop -- ver arriba
      await new Promise((r) => setTimeout(r, ENTRE_TICKETS_MS));
    }
  }, [impresora, venueID, leToca, encolar, imprimirUna, drenarSueltasPendientes]);

  const trasConectar = useCallback((datos) => {
    if (datos) {
      guardarEmparejamiento(venueID, datos);
      setEmparejada(leerEmparejamiento(venueID));
    }
    setConectada(true);
    ultimoLatidoRef.current = 0; // que late ya, sin esperar al siguiente turno
  }, [venueID]);

  /** Abre el selector del sistema. Va SIEMPRE colgado de un botón: exige gesto humano. */
  const conectar = useCallback(async () => {
    setConectando(true);
    try {
      trasConectar(await impresora.conectar(venueID));
      return { ok: true };
    } catch (e) {
      setConectada(false);
      const texto = String(e?.message || "");
      const cancelado = e?.name === "NotFoundError" && /cancel/i.test(texto);
      return { ok: false, cancelado, motivo: cancelado ? "" : (texto || e?.name || "No se pudo conectar") };
    } finally {
      setConectando(false);
    }
  }, [impresora, venueID, trasConectar]);

  /** Reintento sin selector. Hoy ningún navegador lo consigue, pero cuesta cero. */
  const reintentarEnSilencio = useCallback(async () => {
    if (!impresora.disponible || impresora.conectada() || conectando) return;
    if (!leerEmparejamiento(venueID)) return;
    const ahora = Date.now();
    if (ahora - ultimoIntentoRef.current < REINTENTO_MS) return;
    ultimoIntentoRef.current = ahora;
    try {
      const datos = await impresora.reconectar(venueID);
      if (datos) trasConectar(datos);
    } catch {
      /* el chip ya dice que está desconectada */
    }
  }, [impresora, venueID, conectando, trasConectar]);

  /**
   * Cada vuelta del sondeo: decir «sigo aquí» si tenemos la impresora, y enterarse de
   * quién la tiene si no la tenemos. Es lo que permite a las demás pantallas pintar
   * «Imprimiendo en Móvil barra» en vez de ofrecer un botón que se la quitaría.
   */
  const refrescarEstacion = useCallback(async () => {
    const cabeceras = cabecerasRef.current;
    if (!cabeceras || !venueID) return;

    if (impresora.conectada()) {
      const ahora = Date.now();
      if (ahora - ultimoLatidoRef.current >= api.LATIDO_MS) {
        ultimoLatidoRef.current = ahora;
        const viva = await api.latir(venueID, leerEtiqueta(venueID), cabeceras);
        if (viva) setEstacion(viva);
      }
      return;
    }
    setEstacion(await api.verEstacion(cabeceras));
  }, [impresora, venueID]);

  /** Repetir un ticket a mano, en el aparato que tiene la impresora. */
  const reimprimir = useCallback(async (order) => {
    if (!impresora.conectada()) {
      avisar(order, "La impresora no está conectada.");
      return;
    }
    if (!(await mandar(order))) avisar(order, "No se pudo imprimir el ticket.");
  }, [impresora, mandar, avisar]);

  /** Pedirlo desde una pantalla SIN impresora: lo saca la estación en su sondeo. */
  const pedirReimpresion = useCallback(async (order) => {
    const hecho = await api.pedirReimpresion(order.id, cabecerasRef.current);
    avisar(order, hecho
      ? "Reimpresión pedida: saldrá en la impresora de barra."
      : "No se pudo pedir la reimpresión.");
  }, [avisar]);

  const imprimirPrueba = useCallback(() => reimprimir({
    id: "prueba", fulfillment: "pickup", customer_name: "Ticket de prueba",
    created_at: new Date().toISOString(), total: 0,
    items: [{ id: "1", quantity: 1, product_name: "Si lees esto, funciona" }],
  }), [reimprimir]);

  const olvidar = useCallback(() => {
    impresora.desconectar();
    olvidarEmparejamiento(venueID);
    setEmparejada(null);
    setConectada(false);
    colaRef.current = [];
    setEnCola(0);
  }, [impresora, venueID]);

  // A propósito NO se desconecta al desmontar: salir de la Barra y volver no puede
  // costar un toque de reconexión. La impresora se suelta solo con "Olvidar".

  return {
    disponible: impresora.disponible,
    conectada, conectando, emparejada, papel, enCola, estacion,
    conectar, reintentarEnSilencio, sincronizarEstado, refrescarEstacion,
    revisarCola, reimprimir, pedirReimpresion, imprimirPrueba, olvidar,
  };
}
